import { getEngagementsBaseUrl } from "src/config/Endpoints";
import { getTenantId } from "src/utils/getters";
import i18n from "src/i18n/config";
import { getApiLangFromI18n, normalizeApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";
import type { EngagementCampaignFilter, EngagementMedium } from "./EngagementOverviewAPI";

const ENGAGEMENTS_BASE_URL = getEngagementsBaseUrl();


interface CampaignEnvelope<TData> {
  status?: string;
  data: TData;
}

interface CampaignListEnvelope<TData> extends CampaignEnvelope<TData> {
  pagination: CampaignPagination;
}

export type CampaignSource = EngagementMedium;

export type CampaignStatus = "draft" | "scheduled" | "sent" | "active" | "archive";

export type CampaignSortOrder = "asc" | "desc";

export type CampaignSortBy =
  | "campaign_id"
  | "campaign_name"
  | "source"
  | "sent_at"
  | "scheduled"
  | "audience"
  | "status";

export interface CampaignPagination {
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

export interface CampaignListMetrics {
  emails_sent: number;
  opens: number;
  conversions: number;
  audience: number;
  total_users: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  conversion_rate: number;
  emails_sentFormattedValue?: string | null;
  opensFormattedValue?: string | null;
  conversionsFormattedValue?: string | null;
  audienceFormattedValue?: string | null;
  total_usersFormattedValue?: string | null;
  open_rateFormattedValue?: string | null;
  click_rateFormattedValue?: string | null;
  bounce_rateFormattedValue?: string | null;
  conversion_rateFormattedValue?: string | null;
}

export interface CampaignListRow {
  campaign_id: string;
  /** `null` for a draft saved before a name was typed. */
  campaign_name: string | null;
  source: CampaignSource;
  source_label?: string;
  sent_at: string;
  scheduled: string;
  audience: string;
  status: CampaignStatus;
  status_label?: string;
  /** Set only on rows backed by a wizard draft; `null` on campaigns. */
  draft_id?: number | null;
  current_step?: number | null;
  metrics?: CampaignListMetrics | null;
}

export interface CampaignsListRequest {
  start_date: string;
  end_date: string;
  campaign_name?: EngagementCampaignFilter;
  source?: CampaignSource | CampaignSource[] | null;
  status?: CampaignStatus | CampaignStatus[] | null;
  search?: string | null;
  page?: number;
  page_size?: number;
  sort_by?: CampaignSortBy;
  sort_order?: CampaignSortOrder;
  refresh?: boolean;
}

export interface CampaignsListResult {
  data: CampaignListRow[];
  pagination: CampaignPagination;
}

export interface CampaignDetailsBreakdown {
  delivered: number;
  bounces: number;
  bounce_drops: number;
  spam_report_drops: number;
  other: number;
}

export interface CampaignDetailsEngagement {
  opens: number;
  unique_opens: number;
  clicks: number;
  unique_clicks: number;
  unsubscribes: number;
  spam_reports: number;
}

export interface CampaignDetailsRates {
  open_rate: number;
  click_rate: number;
  conversion_rate: number;
  unsubscribe_rate: number;
}

export interface CampaignDetailsData {
  campaign_id: string;
  campaign_name: string;
  source: CampaignSource;
  sent: number;
  breakdown: CampaignDetailsBreakdown;
  engagement: CampaignDetailsEngagement;
  rates: CampaignDetailsRates;
  conversions: number;
  total_users: number;
}

export interface CampaignDetailsRequest {
  campaign_id: string;
  refresh?: boolean;
}

export interface CampaignComparisonRow {
  campaign_id: string;
  campaign_name: string;
  source: CampaignSource;
  delivered: number;
  open_rate: number;
  ctr: number;
  bounce_rate: number;
  conversion_rate: number;
  unsubscribe_rate: number;
  deliveredFormattedValue?: string | null;
  open_rateFormattedValue?: string | null;
  ctrFormattedValue?: string | null;
  bounce_rateFormattedValue?: string | null;
  conversion_rateFormattedValue?: string | null;
  unsubscribe_rateFormattedValue?: string | null;
}

export interface CampaignComparisonData {
  start_date: string;
  end_date: string;
  source: CampaignSource[] | null;
  total_campaigns: number;
  campaigns: CampaignComparisonRow[];
}

export interface CampaignComparisonRequest {
  start_date: string;
  end_date: string;
  source?: CampaignSource[] | null;
  campaign_name?: EngagementCampaignFilter;
  refresh?: boolean;
}

export interface CampaignArchiveResultRow {
  campaign_id: string;
  campaign_name: string;
  previous_status: CampaignStatus;
  status: CampaignStatus;
}

export interface CampaignArchiveData {
  archived: boolean;
  updated: number;
  campaigns: CampaignArchiveResultRow[];
  not_found: string[];
}

export interface CampaignArchiveRequest {
  campaign_id: string | string[];
  archived: boolean;
}

export interface CampaignDeleteData {
  deleted: number;
  campaign_ids: string[];
  not_found: string[];
}

export interface CampaignDeleteRequest {
  campaign_id: string | string[];
}

const requestCampaign = async <TEnvelope,>(
  path: string,
  method: "POST" | "PUT" | "DELETE",
  body: Record<string, unknown>,
): Promise<TEnvelope> => {
  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await fetch(`${ENGAGEMENTS_BASE_URL}/${path}?${params.toString()}`, {
    method,
    headers: buildAuthHeaders("api_key"),
    body: JSON.stringify({ tenant_id: Number(getTenantId()), ...body }),
  });
  return handleJsonResponse<TEnvelope>(response);
};

export const campaignPerformanceService = {

  fetchCampaignsList: async ({
    refresh = false,
    ...request
  }: CampaignsListRequest): Promise<CampaignsListResult> => {
    const payload = await requestCampaign<CampaignListEnvelope<CampaignListRow[]>>(
      "campaigns-list",
      "POST",
      { ...request, refresh },
    );
    return {
      data: payload.data ?? [],
      pagination: payload.pagination,
    };
  },

  fetchCampaignDetails: async ({
    refresh = false,
    ...request
  }: CampaignDetailsRequest): Promise<CampaignDetailsData> => {
    const payload = await requestCampaign<CampaignEnvelope<CampaignDetailsData>>(
      "campaign-details",
      "POST",
      { ...request, refresh },
    );
    return payload.data;
  },

  fetchCampaignComparison: async ({
    refresh = false,
    ...request
  }: CampaignComparisonRequest): Promise<CampaignComparisonData> => {
    const payload = await requestCampaign<CampaignEnvelope<CampaignComparisonData>>(
      "campaign-comparison",
      "POST",
      { ...request, refresh },
    );
    return payload.data;
  },

  /** Local only — nothing changes in SendGrid. */
  archiveCampaigns: async (request: CampaignArchiveRequest): Promise<CampaignArchiveData> => {
    const payload = await requestCampaign<CampaignEnvelope<CampaignArchiveData>>(
      "campaigns-archive",
      "PUT",
      { ...request },
    );
    return payload.data;
  },

  deleteCampaigns: async (request: CampaignDeleteRequest): Promise<CampaignDeleteData> => {
    const payload = await requestCampaign<CampaignEnvelope<CampaignDeleteData>>(
      "campaigns-delete",
      "DELETE",
      { ...request },
    );
    return payload.data;
  },
};
